import { useMedplum } from '@medplum/react-hooks';
import type { Appointment } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  executeChimeTelehealth,
  type ChimeEndResult,
  type ChimeJoinResult,
} from '@/lib/telehealth/chime/chimeTelehealthService';
import { toDeviceOptions } from '@/lib/telehealth/chime/chimeDevices';
import { useChimeMeetingSession } from '@/lib/telehealth/chime/useChimeMeetingSession';
import { useChimeRecordingShown } from '@/lib/telehealth/chime/useChimeRecordingShown';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import type { RecordingAudioDeviceOption } from '@/pages/session/tabs/RecordingControlsTab';
import type {
  InPersonHostStatus,
  InPersonRecordingSetupPhase,
  UseInPersonRecordingSessionResult,
} from './inPersonRecordingTypes';
import {
  decideInPersonDisconnect,
  inPersonReconnectNotice,
  inPersonRecordingStopped,
  runInPersonDisconnect,
  type InPersonReconnectState,
} from './inPersonDisconnect';
import { acquireInPersonMicrophone, runInPersonEnd, runInPersonJoin } from './inPersonJoin';
import { installRecordingLeaveGuard, type RecordingLeaveGuardEnv } from './recordingLeaveGuard';
import { KEEP_SCREEN_ON_TEXT, requestScreenWakeLock, type ScreenWakeLock } from './wakeLock';

export const IN_PERSON_END_DECLINED_TEXT =
  'This recording was replaced by a newer session on another device and was not ended. End it from that device, or reload this page.';
export const IN_PERSON_RECORDING_STOPPED_TEXT = 'Recording stopped. End the session, or start it again.';

function setupPhaseOf(status: InPersonHostStatus): InPersonRecordingSetupPhase {
  if (status === 'starting') return 'setting-up';
  if (status === 'error') return 'failed';
  return 'consent';
}

/**
 * In-person recording on Chime (design Decision 11): one audio-only provider attendee in a meeting
 * created on demand, run in the appointment page itself.
 *   - A Screen Wake Lock is held while recording and re-acquired when the page is visible again;
 *     without one the page says "Keep this screen on while recording".
 *   - When the device wakes with the media connection gone, the page rejoins (`provider-join` with
 *     `inPerson`), which keeps the capture or, after a long sleep, starts a new segment.
 *   - Only End stops the recording (`end` with the current `expectedMeetingId`); leaving the page
 *     sends no `leave`. The one other `end` is a failed first start: if `provider-join` succeeded but the
 *     local media then fails, the capture is ended best effort so it does not run unattended.
 */
/** How long a started in-person capture shows as recording before the recorder must be in the roster. */
const IN_PERSON_RECORDER_HOLD_MS = 60_000;

export function useChimeInPersonRecording(
  appointmentId: string | undefined,
  onAppointmentUpdated: (appointment: Appointment) => void,
): UseInPersonRecordingSessionResult & { notice: string | null; elsewhereNotice: string | null } {
  const medplum = useMedplum();
  const session = useChimeMeetingSession();
  const [hostStatus, setHostStatus] = useState<InPersonHostStatus>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);
  // Two independent notices: the missing wake lock, and capture having stopped.
  const [wakeLockMissing, setWakeLockMissing] = useState(false);
  // Recording comes from Chime's roster: recording while the recorder attendee is present.
  // No poll here, so a started capture is held as recording for up to a minute while the recorder joins the roster.
  const { recording, expect: expectRecording } = useChimeRecordingShown(session.recorderPresent, undefined, IN_PERSON_RECORDER_HOLD_MS);
  const recordingStopped = inPersonRecordingStopped({ hostStatus, sessionStatus: session.status, recording });
  const [endDeclined, setEndDeclined] = useState(false);
  // Shown on the appointment page (outside the recording controls, which are hidden once the room is lost).
  // True while an End is in flight (a ref, so the disconnect effect and the End see the same value at once).
  const endingRef = useRef(false);
  const [elsewhereNotice, setElsewhereNotice] = useState<string | null>(null);
  const [reconnect, setReconnect] = useState<InPersonReconnectState>('idle');
  // One automatic rejoin per loss: a failed rejoin must not loop; it stays an error until the session recovers.
  const rejoinAttemptedRef = useRef(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [ending, setEnding] = useState(false);
  const [audioDevices, setAudioDevices] = useState<RecordingAudioDeviceOption[]>([]);
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | null>(null);
  // Mirrors the selection for `join`, which the rejoin effects hold onto across renders.
  const audioDeviceIdRef = useRef<string | undefined>();
  const meetingIdRef = useRef<string | undefined>();
  const startedAtRef = useRef<number | null>(null);
  const wakeLockRef = useRef<ScreenWakeLock | undefined>();
  const joiningRef = useRef(false);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const holdWakeLock = useCallback(async () => {
    if (wakeLockRef.current) return;
    wakeLockRef.current = await requestScreenWakeLock(globalThis.navigator as never);
    setWakeLockMissing(!wakeLockRef.current);
  }, []);

  const releaseWakeLock = useCallback(() => {
    void wakeLockRef.current?.release().catch(() => undefined);
    wakeLockRef.current = undefined;
  }, []);

  /**
   * `provider-join` with `inPerson`, then the audio-only media. The bot refuses when it cannot record.
   * A first start (`endOnFailure`) that fails after `provider-join` ends the capture best effort.
   */
  const join = useCallback(
    async (endOnFailure = false): Promise<boolean> => {
      if (!appointmentId || joiningRef.current) return false;
      joiningRef.current = true;
      try {
        await runInPersonJoin({
          providerJoin: async () => {
            const result = (await executeChimeTelehealth(medplum, {
              action: 'provider-join',
              appointmentId,
              inPerson: true,
            })) as ChimeJoinResult;
            // The bot refuses an in-person join it cannot record; show recording until the recorder joins the roster.
            expectRecording(result.recordingActive ? true : undefined);
            return result;
          },
          // `requireAudio`: no chosen microphone fails the start (ending the capture) instead of recording silence.
          startMedia: (meeting, attendee) =>
            sessionRef.current.start(meeting, attendee, {
              audioDeviceId: audioDeviceIdRef.current,
              requireAudio: true,
            }),
          onMeeting: (meetingId) => {
            meetingIdRef.current = meetingId;
          },
          end: (meetingId) =>
            executeChimeTelehealth(medplum, {
              action: 'end',
              appointmentId,
              inPerson: true,
              expectedMeetingId: meetingId,
            }),
          endOnFailure,
        });
        return true;
      } finally {
        joiningRef.current = false;
      }
    },
    [appointmentId, expectRecording, medplum],
  );

  const beginRecording = useCallback(async () => {
    setHostStatus('starting');
    setJoinError(null);
    setReconnect('idle');
    setElsewhereNotice(null);
    rejoinAttemptedRef.current = false;
    try {
      // Permission first, so the list has real ids and a microphone is chosen before the capture starts.
      const microphone = await acquireInPersonMicrophone(navigator.mediaDevices, audioDeviceIdRef.current);
      setAudioDevices(toDeviceOptions(microphone.devices, 'Microphone'));
      audioDeviceIdRef.current = microphone.deviceId;
      setSelectedAudioDeviceId(microphone.deviceId);
      await join(true);
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      setHostStatus('joined');
      await holdWakeLock();
    } catch (e) {
      // The bot's refusal ("Recording needs the client's AI documentation consent") is shown as is.
      setJoinError(getUserFacingErrorMessage(e, 'Could not start recording.'));
      meetingIdRef.current = undefined;
      setHostStatus('error');
    }
  }, [holdWakeLock, join]);

  // Re-acquire the wake lock (the browser drops it when the page is hidden), and rejoin when the
  // device woke with the media connection gone.
  useEffect(() => {
    if (hostStatus !== 'joined') return undefined;
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      wakeLockRef.current = undefined;
      void holdWakeLock();
      if (sessionRef.current.status !== 'in-meeting') {
        join().catch((e: unknown) => setJoinError(getUserFacingErrorMessage(e, 'Could not resume recording.')));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [holdWakeLock, hostStatus, join]);

  // The media session ended in `error` while recording. AudioAttendeeRemoved means Start was pressed on another device
  // (this attendee was evicted): stop here and never rejoin, which would evict that device in turn. Any other terminal
  // code rejoins once, as the wake-up path does, with a reconnect notice that persists (as an error if the rejoin fails).
  useEffect(() => {
    if (session.status === 'in-meeting') {
      rejoinAttemptedRef.current = false;
      setReconnect('idle');
      return;
    }
    const action = decideInPersonDisconnect({
      hostStatus,
      sessionStatus: session.status,
      errorCode: session.errorCode,
      endedByHost: session.endedByHost,
      ending: endingRef.current,
    });
    if (action === 'none' || (action === 'rejoin' && rejoinAttemptedRef.current)) return;
    if (action === 'rejoin') rejoinAttemptedRef.current = true;
    void runInPersonDisconnect(action, {
      stopLocally: () => {
        sessionRef.current.stop();
        releaseWakeLock();
      },
      onTakenOver: (text) => {
        meetingIdRef.current = undefined;
        startedAtRef.current = null;
        // No stale notices for a later Start.
        setWakeLockMissing(false);
        setReconnect('idle');
        setElsewhereNotice(text);
        setJoinError(text);
        setHostStatus('error');
      },
      rejoin: async () => {
        // `join` answers false when a join is already running or there is no appointment: not a recovery.
        if (!(await join())) throw new Error('Could not resume recording.');
      },
      onReconnectState: setReconnect,
      onRejoinError: (e) => setJoinError(getUserFacingErrorMessage(e, 'Could not resume recording.')),
    });
  }, [hostStatus, join, releaseWakeLock, session.endedByHost, session.errorCode, session.status]);

  // Leaving the page would orphan a running recording: warn on close/reload and confirm in-app links
  // while the session is starting or joined. End (or unmount) removes the guard.
  useEffect(() => {
    if (hostStatus !== 'starting' && hostStatus !== 'joined') return undefined;
    return installRecordingLeaveGuard({ window, document } as unknown as RecordingLeaveGuardEnv);
  }, [hostStatus]);

  // Elapsed timer. Recording state needs no poll: it is the recorder's roster presence.
  useEffect(() => {
    if (hostStatus !== 'joined' || !appointmentId) return undefined;
    const tick = window.setInterval(() => {
      if (startedAtRef.current) setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(tick);
  }, [appointmentId, hostStatus]);

  useEffect(() => releaseWakeLock, [releaseWakeLock]);

  const endSession = useCallback(async (): Promise<boolean> => {
    if (!appointmentId) return false;
    setEnding(true);
    endingRef.current = true;
    try {
      setEndDeclined(false);
      const outcome = await runInPersonEnd({
        end: () =>
          executeChimeTelehealth(medplum, {
            action: 'end',
            appointmentId,
            inPerson: true,
            expectedMeetingId: meetingIdRef.current,
          }) as Promise<ChimeEndResult>,
        // The server ignored this End (a newer session on another device replaced the meeting): nothing ended,
        // so keep this page's state, media and wake lock and say so.
        onDeclined: () => setEndDeclined(true),
        onEnded: async () => {
          sessionRef.current.stop();
          releaseWakeLock();
          meetingIdRef.current = undefined;
          startedAtRef.current = null;
          setHostStatus('idle');
          setWakeLockMissing(false);
          setReconnect('idle');
          // The End succeeded: nothing from a MeetingEnded that raced it may linger as a takeover.
          setElsewhereNotice(null);
          setJoinError(null);
          onAppointmentUpdated(await medplum.readResource('Appointment', appointmentId, { cache: 'no-cache' }));
        },
      });
      return outcome === 'ended';
    } finally {
      endingRef.current = false;
      setEnding(false);
    }
  }, [appointmentId, medplum, onAppointmentUpdated, releaseWakeLock]);

  const setAudioInputDevice = useCallback((deviceId: string) => {
    const id = deviceId.trim();
    if (!id) return;
    audioDeviceIdRef.current = id;
    setSelectedAudioDeviceId(id);
    void sessionRef.current.setAudioInput(id).catch(() => undefined);
  }, []);

  const clearSetupError = useCallback(() => {
    setJoinError(null);
    setElsewhereNotice(null);
    if (hostStatus === 'error') setHostStatus('idle');
  }, [hostStatus]);

  return {
    hostStatus,
    setupPhase: setupPhaseOf(hostStatus),
    joinError,
    elsewhereNotice,
    notice:
      [
        inPersonReconnectNotice(reconnect, session.status),
        recordingStopped ? IN_PERSON_RECORDING_STOPPED_TEXT : null,
        wakeLockMissing ? KEEP_SCREEN_ON_TEXT : null,
        endDeclined ? IN_PERSON_END_DECLINED_TEXT : null,
      ]
        .filter(Boolean)
        .join(' ') || null,
    elapsedSeconds,
    isMuted: session.muted,
    ending,
    starting: hostStatus === 'starting',
    showRecordingControls: hostStatus === 'joined',
    audioDevices,
    selectedAudioDeviceId,
    beginRecording,
    toggleMute: session.toggleMute,
    setAudioInputDevice,
    endSession,
    clearSetupError,
  };
}
